import { useEffect, useMemo } from 'react'
import { Paperclip } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { ArtifactSubmissionLink } from '@/lib/api'
import { cn } from '@/lib/utils'
import { shortSubmissionTitle } from '@/lib/submission-title'
import { useExtractionStore } from '@/stores/extraction-store'

interface SubmissionLinksIndicatorProps {
  bidId: string
  artifactId: string
  sectionKey: string | null
  links: ArtifactSubmissionLink[]
  onLinked?: () => void | Promise<void>
  /**
   * Table-row mode (the response outline): a linked item shows as a compact
   * neutral tag with its short name (full title in a tooltip), and an unlinked
   * row shows nothing at rest — a "Link" action appears on row hover or focus.
   * The row must carry Tailwind's `group` class for the hover reveal.
   */
  compact?: boolean
}

export function SubmissionLinksIndicator({
  bidId,
  artifactId,
  sectionKey,
  links,
  onLinked,
  compact = false,
}: SubmissionLinksIndicatorProps) {
  const categoryState = useExtractionStore((state) => state.categories.submission_checklist)
  const loadCategory = useExtractionStore((state) => state.loadCategory)
  const linkSubmissionItem = useExtractionStore((state) => state.linkSubmissionItem)
  const { isReadOnly } = useBidReadOnly(bidId)

  useEffect(() => {
    if (categoryState.state === 'empty' && categoryState.items.length === 0) {
      void loadCategory(bidId, 'submission_checklist')
    }
  }, [bidId, categoryState.items.length, categoryState.state, loadCategory])

  const availableItems = useMemo(() => {
    return categoryState.items
      .map((item, index) => ({ ...item, itemNumber: index + 1 }))
      .filter((item): item is typeof item & { id: string; title: string } => {
        return 'title' in item && 'id' in item && typeof item.id === 'string'
      })
      .filter((item) => {
        if (!('linkedArtifactId' in item)) return false
        return !item.linkedArtifactId
      })
  }, [categoryState.items])

  if (links.length > 0 && compact) {
    return (
      <TooltipProvider>
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {links.map((link) => (
            <Tooltip key={link.submissionItemId}>
              <TooltipTrigger asChild>
                <Badge variant="secondary" dot={false} tabIndex={0} className="max-w-full min-w-0 text-xs font-normal">
                  <span className="truncate">{shortSubmissionTitle(link.submissionItemTitle)}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="top">
                {link.submissionItemTitle}
                {link.limit ? ` (${link.limit})` : ''}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    )
  }

  if (links.length > 0) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {links.map((link) => (
          <Badge key={link.submissionItemId} variant="outline" className="gap-1 text-[10px]">
            <Paperclip className="h-3 w-3" />
            <span className="max-w-[180px] truncate">{link.submissionItemTitle}</span>
            {link.limit && <span className="text-muted-foreground">({link.limit})</span>}
          </Badge>
        ))}
      </div>
    )
  }

  if (categoryState.state === 'empty' && categoryState.items.length === 0) {
    return null
  }

  // Linking is a write; a read-only user (an org viewer, or a member who is not
  // on the bid) just sees the links that exist.
  if (isReadOnly) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        {compact ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Link to submission"
            className={cn(
              'h-6 px-2 text-meta text-[hsl(var(--link))]',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity'
            )}
          >
            Link
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-meta">
            Link to submission
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1">
        {availableItems.length === 0 ? (
          <div className="px-2 py-3 text-meta">
            No unlinked submission items available.
          </div>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {availableItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
                onClick={() => void (async () => {
                  await linkSubmissionItem(bidId, item.id, artifactId, sectionKey)
                  await onLinked?.()
                })()}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">
                    <span className="text-muted-foreground">{item.itemNumber}.</span>{' '}
                    {item.title}
                  </div>
                  {'limit' in item && item.limit && (
                    <div className="text-[10px] text-muted-foreground">{item.limit}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
